import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, X, Plus, Edit, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ContentEditor from '@/components/admin/ContentEditor';

interface PageContent {
  id: string;
  page_name: string;
  section_name: string;
  content_type: string;
  content: string;
  order_index: number;
}

interface PageEditorProps {
  pageId: string;
  onClose: () => void;
}

const PageEditor = ({ pageId, onClose }: PageEditorProps) => {
  const [contents, setContents] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingContent, setEditingContent] = useState<PageContent | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const contentTypes = [
    { value: 'text', label: 'טקסט' },
    { value: 'html', label: 'HTML' },
    { value: 'title', label: 'כותרת' },
    { value: 'paragraph', label: 'פסקה' },
    { value: 'image', label: 'תמונה' },
  ];

  useEffect(() => {
    fetchPageContents();
  }, [pageId]);

  const fetchPageContents = async () => {
    try {
      const { data, error } = await supabase
        .from('page_contents')
        .select('*')
        .eq('page_name', pageId)
        .order('order_index');

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Error fetching page contents:', error);
      toast.error('שגיאה בטעינת תוכן העמוד');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async (content: Partial<PageContent>) => {
    setSaving(true);
    try {
      if (content.id) {
        // Update existing content
        const { error } = await supabase
          .from('page_contents')
          .update({
            section_name: content.section_name,
            content_type: content.content_type,
            content: content.content,
            order_index: content.order_index,
          })
          .eq('id', content.id);

        if (error) throw error;
        toast.success('התוכן עודכן בהצלחה');
      } else {
        // Create new content
        const { error } = await supabase
          .from('page_contents')
          .insert({
            page_name: pageId,
            section_name: content.section_name!,
            content_type: content.content_type!,
            content: content.content!,
            order_index: content.order_index || 0,
          });

        if (error) throw error;
        toast.success('התוכן נוסף בהצלחה');
      }

      await fetchPageContents();
      setEditingContent(null);
      setIsAddingNew(false);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('שגיאה בשמירת התוכן');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק תוכן זה?')) return;

    try {
      const { error } = await supabase
        .from('page_contents')
        .delete()
        .eq('id', contentId);

      if (error) throw error;
      toast.success('התוכן נמחק בהצלחה');
      await fetchPageContents();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('שגיאה במחיקת התוכן');
    }
  };

  const getPageDisplayName = (pageId: string) => {
    const pageMap: Record<string, string> = {
      'homepage': 'עמוד הבית',
      'about': 'אודות',
      'contact': 'צור קשר',
      'terms': 'תנאי שימוש',
      'privacy': 'מדיניות פרטיות',
    };
    return pageMap[pageId] || pageId;
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p>טוען...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center justify-between">
            <span>עריכת תוכן - {getPageDisplayName(pageId)}</span>
            <div className="flex items-center gap-2">
              {pageId === 'homepage' && (
                <>
                  <ContentEditor componentType="WhatWeDo" onSave={fetchPageContents} />
                  <ContentEditor componentType="WhyBuyFromUs" onSave={fetchPageContents} />
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddingNew(true)}
              >
                <Plus className="h-4 w-4 ml-2" />
                הוסף תוכן
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {contents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">אין תוכן בעמוד זה</p>
              <Button onClick={() => setIsAddingNew(true)}>
                <Plus className="h-4 w-4 ml-2" />
                הוסף תוכן ראשון
              </Button>
            </div>
          ) : (
            contents.map((content) => (
              <Card key={content.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">
                      {content.section_name} ({contentTypes.find(t => t.value === content.content_type)?.label})
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingContent(content)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteContent(content.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    {content.content.length > 100 
                      ? content.content.substring(0, 100) + '...'
                      : content.content
                    }
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Content Editor Modal */}
        {(editingContent || isAddingNew) && (
          <ContentEditModal
            content={editingContent}
            pageId={pageId}
            contentTypes={contentTypes}
            onSave={handleSaveContent}
            onCancel={() => {
              setEditingContent(null);
              setIsAddingNew(false);
            }}
            saving={saving}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface ContentEditModalProps {
  content: PageContent | null;
  pageId: string;
  contentTypes: { value: string; label: string }[];
  onSave: (content: Partial<PageContent>) => void;
  onCancel: () => void;
  saving: boolean;
}

const ContentEditModal = ({ content, pageId, contentTypes, onSave, onCancel, saving }: ContentEditModalProps) => {
  const [formData, setFormData] = useState({
    section_name: content?.section_name || '',
    content_type: content?.content_type || 'text',
    content: content?.content || '',
    order_index: content?.order_index || 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: content?.id,
    });
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {content ? 'עריכת תוכן' : 'הוספת תוכן חדש'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="section_name">שם קטע</Label>
            <Input
              id="section_name"
              value={formData.section_name}
              onChange={(e) => setFormData(prev => ({ ...prev, section_name: e.target.value }))}
              placeholder="לדוגמה: כותרת ראשית, תיאור החברה"
              required
              dir="rtl"
              className="text-right"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content_type">סוג תוכן</Label>
            <Select
              value={formData.content_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}
            >
              <SelectTrigger dir="rtl">
                <SelectValue placeholder="בחר סוג תוכן" />
              </SelectTrigger>
              <SelectContent>
                {contentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="order_index">סדר הצגה</Label>
            <Input
              id="order_index"
              type="number"
              value={formData.order_index}
              onChange={(e) => setFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
              min="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">תוכן</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="הזן את התוכן כאן..."
              rows={8}
              required
              dir="rtl"
              className="text-right"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 ml-2" />
              ביטול
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 ml-2" />
              {saving ? 'שומר...' : 'שמור'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PageEditor;